import { DviNotePresetGroup } from '../../services/api/dviNotePresetsApi';

/**
 * Client-side mirror of DviNotePresetModel.resolveForPoint (WP-07).
 *
 * WHY CLIENT-SIDE. The server exposes GET /dvi-note-presets/resolve, but an
 * inspection renders 30-plus points at once and calling it per row would be a
 * request storm — the same shape of bug already fixed once in this component
 * tree. The form fetches every group in ONE request and each row resolves
 * locally against that list.
 *
 * The precedence MUST match the server's, because the same lookup happens
 * server-side wherever a single point is resolved: most specific first —
 * exact point name, then category, then the shop-wide catch-all.
 *
 * Comparison is case-insensitive for the same reason as the SQL's COLLATE
 * NOCASE: `point_name` and `category_name` are free text copied from a
 * template, while the owner types the scope by hand in settings.
 */
export function resolvePresetGroup(
  groups: DviNotePresetGroup[] | null | undefined,
  pointName: string | null | undefined,
  categoryName: string | null | undefined,
): DviNotePresetGroup | null {
  if (!groups || groups.length === 0) return null;

  const active = groups.filter((g) => g.is_active !== 0);
  const eq = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

  if (pointName) {
    const byPoint = active.find((g) => g.scope_type === 'point' && eq(g.scope_value, pointName));
    if (byPoint) return byPoint;
  }
  if (categoryName) {
    const byCategory = active.find(
      (g) => g.scope_type === 'category' && eq(g.scope_value, categoryName),
    );
    if (byCategory) return byCategory;
  }
  return active.find((g) => g.scope_type === 'shop') ?? null;
}

/**
 * Render a measurement for display: "Wear: 2mm", "Wear Amount: 3/32".
 *
 * THE UNIT TRAP. A shop can express the same measurement two ways, and both are
 * legitimate: value "2" with unit "mm", or value "2mm" with unit "mm" (which is
 * what you get naturally when the preset list reads 2mm / 3mm / 4mm and the unit
 * column is also filled in). Blindly appending the unit turns the second case
 * into "2mm mm" — which shipped straight onto the customer's estimate and the
 * invoice before this was caught. So the unit is appended only when the value
 * does not already end with it.
 *
 * Case-insensitive because the owner types both fields by hand.
 */
export function formatMeasurement(
  value: string | null | undefined,
  unit?: string | null,
  label?: string | null,
): string {
  const v = (value ?? '').trim();
  if (!v) return '';

  const u = (unit ?? '').trim();
  const alreadyCarriesUnit = !!u && v.toLowerCase().endsWith(u.toLowerCase());
  const withUnit = u && !alreadyCarriesUnit ? `${v} ${u}` : v;

  const l = (label ?? '').trim();
  return l ? `${l}: ${withUnit}` : withUnit;
}

export default resolvePresetGroup;
